// PR #974 (AI cockpit): the docked assistant panel hosted by the app shell.
//
// The shell owns the router, so this is where a chat surface beside the current
// route belongs. The panel deliberately reuses the existing primitives instead of
// inventing a second chat stack:
//   - `useEntityChat` + `useChatConversations` for the conversation and transport
//     (the same native runtime `/chat` uses — not `EntityChatPanel` itself, which
//     owns its own header and does not expose the canvas/entity callbacks);
//   - `AntDesignXChatSurface` for the message stream, approvals and structured
//     payload renderers;
//   - `ArtifactCanvas` and `EntityOverlayDrawer` for the canvas and the record
//     overlay, wired through the surface's existing callbacks;
//   - `useChatActionHandlers` for the existing, approval-gated action handlers.
//
// No mutation path is added: writes stay server-owned and approval-gated, exactly
// as on `/chat`.
//
// The panel is mounted only while it is open and the route is supported, so a
// closed cockpit (or one behind a default-off flag) creates no chat queries at
// all. The `enabled: scope !== null` gate additionally keeps an anonymous
// session from issuing list requests.

import { useTranslation } from '@govard/i18n-core/react';
import { Button, Drawer, Grid } from 'antd';
import { PanelRightClose } from 'lucide-react';
import { useEffect, useId, useState, type ReactElement } from 'react';

import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { AntDesignXChatSurface } from './AntDesignXChatSurface.js';
import { ArtifactCanvas, type ArtifactContent } from './ArtifactCanvas.js';
import { CockpitActionChips } from './CockpitActionChips.js';
import { EntityOverlayDrawer, type EntityOverlayItem } from './EntityOverlayDrawer.js';
import { useOpenRouterModel } from './OpenRouterModelSelector.js';
import { DEFAULT_CHAT_GATEWAY } from '../lib/available-chat-gateways.js';
import { chatCockpitSection, type ChatCockpitContext } from '../lib/chatCockpitContext.js';
import { useChatConversations } from '../hooks/useChatConversations.js';
import { useEntityChat } from '../hooks/useEntityChat.js';
import { useChatActionHandlers } from '../hooks/useChatActionHandlers.js';

const ENTITY_TYPE_LABEL_KEY: Record<'deal' | 'contact' | 'project', string> = {
  deal: 'crm.shared.entity_type.deal',
  contact: 'crm.shared.entity_type.contact',
  project: 'crm.shared.entity_type.project',
};

export interface ChatCockpitPanelProps {
  /** Validated context derived from the current route + selection. */
  context: ChatCockpitContext;
  onClose: () => void;
}

export function ChatCockpitPanel({ context, onClose }: ChatCockpitPanelProps): ReactElement {
  useI18nSubscription();
  const { t } = useTranslation();
  const scope = useCurrentScope();
  const screens = Grid.useBreakpoint();
  const isDesktop = screens.lg !== false;

  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [activeArtifact, setActiveArtifact] = useState<ArtifactContent | null>(null);
  const [activeEntity, setActiveEntity] = useState<EntityOverlayItem | null>(null);
  const [openRouterModel] = useOpenRouterModel();
  // The visible panel title names the surface in both layouts: the desktop aside
  // and the narrow-screen drawer point their accessible name at it instead of
  // duplicating the string in `aria-label`, so the name can never drift from
  // what the person actually reads.
  const titleId = useId();

  const conversations = useChatConversations(
    scope,
    { contextEntityType: context.entityType, contextEntityId: context.entityId },
    { enabled: scope !== null },
  );
  const selectedConversation = conversations.conversations.find(
    (conversation) => conversation.id === selectedConversationId,
  );

  // A different record (or session) is a different conversation: the previous
  // selection must not follow the user onto the next card.
  useEffect(() => {
    setSelectedConversationId(null);
  }, [context.entityId, context.entityType, scope?.workspaceId, scope?.userId]);

  useEffect(() => {
    if (selectedConversationId !== null || conversations.conversations.length === 0) {
      return;
    }
    setSelectedConversationId(conversations.conversations[0]?.id ?? null);
  }, [conversations.conversations, selectedConversationId]);

  const gatewayProfile = selectedConversation?.gatewayProfile ?? DEFAULT_CHAT_GATEWAY;
  const agent = useEntityChat(scope, {
    contextEntityType: context.entityType,
    contextEntityId: context.entityId,
    ...(selectedConversationId !== null ? { conversationId: selectedConversationId } : {}),
    gatewayProfile,
    ...(gatewayProfile === 'openrouter' ? { openRouterModel } : {}),
  });
  const chatDisabled = scope === null || agent.isStreaming;
  const actionHandlers = useChatActionHandlers({
    agent,
    dealId: context.entityType === 'deal' ? context.entityId : undefined,
  });

  const contextText =
    context.entityType === 'none'
      ? context.entityLabel !== ''
        ? context.entityLabel
        : t('crm.ai_chat.cockpit.context_none')
      : t('crm.ai_chat.cockpit.context_value', {
          type: t(ENTITY_TYPE_LABEL_KEY[context.entityType]),
          value: context.entityLabel !== '' ? context.entityLabel : context.entityId,
        });
  const hasChips =
    chatCockpitSection(context.route) === 'deals' ||
    chatCockpitSection(context.route) === 'contacts';

  const body = (
    <div
      className="flex h-full min-h-0 flex-col bg-[var(--ant-color-bg-container)]"
      data-testid="crm-chat-cockpit-panel"
    >
      <div
        className="flex flex-none items-center justify-between gap-2 border-b px-3 py-2"
        style={{ borderColor: 'var(--ant-color-border)' }}
      >
        <div className="min-w-0">
          <p id={titleId} className="truncate text-[13px] font-semibold">
            {t('crm.ai_chat.cockpit.title')}
          </p>
          <p
            className="truncate text-[11px] opacity-70"
            data-testid="crm-chat-cockpit-context"
            title={contextText}
          >
            {contextText}
          </p>
        </div>
        <Button
          type="text"
          size="small"
          icon={<PanelRightClose size={15} />}
          aria-label={t('crm.ai_chat.cockpit.close')}
          title={t('crm.ai_chat.cockpit.close')}
          data-testid="crm-chat-cockpit-close"
          onClick={onClose}
        />
      </div>
      {hasChips ? (
        <div
          className="flex-none border-b px-3 py-2"
          style={{ borderColor: 'var(--ant-color-border)' }}
        >
          <CockpitActionChips context={context} />
        </div>
      ) : null}
      <div className="min-h-0 flex-1 px-3 py-3">
        <AntDesignXChatSurface
          agent={agent}
          placeholder={t('crm.chat.placeholder')}
          submitLabel={t('crm.chat.send')}
          disabled={chatDisabled}
          conversationId={selectedConversationId}
          draftScope={scope ?? undefined}
          contextEntityType={context.entityType === 'none' ? undefined : context.entityType}
          contextEntityId={context.entityId === '' ? undefined : context.entityId}
          onOpenCanvas={setActiveArtifact}
          onOpenEntity={(type, id, title) => {
            setActiveEntity({ type, id, ...(title !== undefined ? { title } : {}) });
          }}
          onApprovalDecisionApplied={agent.applyApprovalDecision}
          onDecideActionPlanItem={agent.decideActionPlanItem}
          onRequestActionPlanUndo={agent.requestActionPlanUndo}
          onRequestActionPlanRetry={agent.requestActionPlanRetry}
          onSendEmailDraft={chatDisabled ? undefined : actionHandlers.onSendEmailDraft}
          onPrepareDealFollowUp={chatDisabled ? undefined : actionHandlers.onPrepareDealFollowUp}
          onCreateDealTasks={chatDisabled ? undefined : actionHandlers.onCreateDealTasks}
          onCreateMeetingTasks={chatDisabled ? undefined : actionHandlers.onCreateMeetingTasks}
          onPrepareMeetingFollowUp={
            chatDisabled ? undefined : actionHandlers.onPrepareMeetingFollowUp
          }
        />
      </div>
    </div>
  );

  return (
    <>
      {isDesktop ? (
        <aside
          role="complementary"
          aria-labelledby={titleId}
          className="flex h-full w-[360px] shrink-0 flex-col border-l"
          style={{ borderColor: 'var(--ant-color-border)' }}
        >
          {body}
        </aside>
      ) : (
        <Drawer
          open
          placement="right"
          size={360}
          onClose={onClose}
          closable={false}
          // Without this the dialog role carried no name at all: the panel draws
          // its own header, so antd's `title` (which would add a second, empty
          // header bar) is not used.
          aria-labelledby={titleId}
          styles={{ body: { padding: 0 } }}
        >
          {body}
        </Drawer>
      )}
      <EntityOverlayDrawer
        item={activeEntity}
        onClose={() => {
          setActiveEntity(null);
        }}
      />
      <ArtifactCanvas
        open={activeArtifact !== null}
        artifact={activeArtifact}
        onClose={() => {
          setActiveArtifact(null);
        }}
      />
    </>
  );
}
